import { hasConsent } from '@/lib/consent';
import { getConfig, CtaType, KPConfig } from './kpConfig';

// ============================================
// Types
// ============================================

export type ScoreBand = 'low' | 'medium' | 'high';

/**
 * Context passed to decidePrimaryCTA
 */
export interface DecisionContext {
  score: number;
  score_band: ScoreBand;
  entry_source?: string;
  utm_source?: string;
  utm_medium?: string;
  referrer?: string;
  day_of_week: number; // 0 = Sunday
  hour: number;
}

/**
 * Full session intelligence for legacy compatibility
 */
export interface KPIntel {
  sessionId: string;
  totalPageViews: number;
  totalSessionTime: number;
  isReturningVisitor: boolean;
  hasIntentSignal: boolean;
  intentActions: string[];
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isSunday: boolean;
  isNightTime: boolean;
  visitHour: number;
  visitDayName: string;
  entrySource?: string;
  utmSource?: string;
  utmMedium?: string;
  referrer?: string;
}

/**
 * Decision output
 */
export interface KPDecision {
  cta_type: CtaType;
  cta_label: string;
  cta_target: string;
  reason_codes: string[];
  score: number;
  score_band: ScoreBand;
  timestamp: number;
  // Legacy aliases
  primary_cta_type: CtaType;
  primary_cta_label: string;
  primary_cta_target: string;
  route_to: string | null;
}

// ============================================
// CTA Label & Target Mapping
// ============================================

const CTA_DEFAULTS: Record<CtaType, { label: string; target: string }> = {
  webinar: {
    label: 'Webinar ansehen',
    target: '/programme', // Programme page has webinar info
  },
  check: {
    label: 'Sleep Check starten',
    target: '/sleep-scan',
  },
  waitlist: {
    label: 'Auf Warteliste',
    target: '/kontakt', // Contact page for waitlist signups
  },
  whatsapp: {
    label: 'WhatsApp schreiben',
    target: 'https://wa.me/4915123456789', // Placeholder - configure in admin
  },
  apply: {
    label: 'Jetzt bewerben',
    target: '/kontakt',
  },
  info: {
    label: 'Mehr Infos',
    target: '/ansatz', // About/Ansatz page for more info
  },
};

// ============================================
// Global State
// ============================================

declare global {
  interface Window {
    kp_decision: Readonly<KPDecision> | null;
  }
}

// ============================================
// Helper Functions
// ============================================

/**
 * Check if hour is within night hours
 */
function isNightHour(hour: number, config: KPConfig): boolean {
  if (config.night_start_hour > config.night_end_hour) {
    // Spans midnight (e.g., 22-6)
    return hour >= config.night_start_hour || hour < config.night_end_hour;
  }
  return hour >= config.night_start_hour && hour < config.night_end_hour;
}

/**
 * Match source against known override patterns
 */
function matchesSource(
  sourceType: 'podcast' | 'eo' | 'linkedin',
  entrySource?: string,
  utmSource?: string,
  utmMedium?: string,
  referrer?: string
): boolean {
  const sources = [
    entrySource?.toLowerCase() || '',
    utmSource?.toLowerCase() || '',
    utmMedium?.toLowerCase() || '',
    referrer?.toLowerCase() || '',
  ];
  
  const sourceString = sources.join(' ');
  
  switch (sourceType) {
    case 'podcast':
      return sourceString.includes('podcast');
    case 'eo':
      return sourceString.includes('eo') || 
             sourceString.includes('entrepreneurs') || 
             sourceString.includes('organisation');
    case 'linkedin':
      return sourceString.includes('linkedin') || sourceString.includes('lnkd');
    default:
      return false;
  }
}

/**
 * Get CTA details for a given type
 */
function getCtaDetails(ctaType: CtaType): { label: string; target: string } {
  return CTA_DEFAULTS[ctaType] || CTA_DEFAULTS.info;
}

/**
 * Calculate score band from score
 */
export function getScoreBand(score: number, config?: KPConfig): ScoreBand {
  const cfg = config || getConfig();
  if (score <= cfg.low_max) return 'low';
  if (score <= cfg.medium_max) return 'medium';
  return 'high';
}

// ============================================
// Main Decision Function
// ============================================

/**
 * Central decision engine for determining the primary CTA
 * 
 * Reads CTA Intelligence settings and applies overrides in order:
 * 1. Time overrides (Sunday, Night)
 * 2. Source overrides (Podcast, EO, LinkedIn)
 * 3. Score-based CTA mapping
 * 
 * @param context - Decision context with score, source, time info
 * @param configOverride - Optional config override (for reactive updates)
 * @returns KPDecision with cta_label, cta_target, reason_codes
 */
export function decidePrimaryCTA(context: DecisionContext, configOverride?: KPConfig): KPDecision {
  const config = configOverride || getConfig();
  const reasonCodes: string[] = [];
  
  // Without statistics consent: no personalisation, fall back to the default (low-band) CTA
  const personalised = hasConsent('statistics');
  const score = personalised ? context.score : 0;
  const score_band = personalised ? context.score_band : 'low';
  const entry_source = personalised ? context.entry_source : 'direct';
  const utm_source = personalised ? context.utm_source : undefined;
  const utm_medium = personalised ? context.utm_medium : undefined;
  const referrer = personalised ? context.referrer : undefined;
  const { day_of_week, hour } = context;
  
  const isSunday = day_of_week === 0;
  const isNight = isNightHour(hour, config);
  
  let selectedCtaType: CtaType;
  let routeOverride: string | null = null;
  
  // ========================================
  // Priority 1: Time overrides
  // ========================================
  
  if (isSunday) {
    selectedCtaType = config.sunday_cta_type;
    reasonCodes.push('time:sunday');
  }
  else if (isNight) {
    selectedCtaType = config.night_cta_type;
    reasonCodes.push(`time:night_${hour}h`);
  }
  
  // ========================================
  // Priority 2: Source overrides
  // ========================================
  
  else if (matchesSource('podcast', entry_source, utm_source, utm_medium, referrer)) {
    selectedCtaType = config.source_podcast_cta_type;
    reasonCodes.push('source:podcast');
  }
  else if (matchesSource('eo', entry_source, utm_source, utm_medium, referrer)) {
    selectedCtaType = config.source_eo_cta_type;
    reasonCodes.push('source:eo');
  }
  else if (matchesSource('linkedin', entry_source, utm_source, utm_medium, referrer)) {
    selectedCtaType = config.source_linkedin_cta_type;
    reasonCodes.push('source:linkedin');
  }
  
  // ========================================
  // Priority 3: Score band mapping
  // ========================================
  
  else {
    switch (score_band) {
      case 'low':
        selectedCtaType = config.cta_low_type;
        routeOverride = config.route_low_to || null;
        reasonCodes.push('score:low');
        break;
      case 'medium':
        selectedCtaType = config.cta_medium_type;
        routeOverride = config.route_medium_to || null;
        reasonCodes.push('score:medium');
        break;
      case 'high':
      default:
        selectedCtaType = config.cta_high_type;
        routeOverride = config.route_high_to || null;
        reasonCodes.push('score:high');
        break;
    }
  }
  
  // ========================================
  // Build decision object
  // ========================================
  
  const ctaDetails = getCtaDetails(selectedCtaType);
  const finalTarget = routeOverride || ctaDetails.target;
  
  const decision: KPDecision = {
    // Primary fields
    cta_type: selectedCtaType,
    cta_label: ctaDetails.label,
    cta_target: finalTarget,
    reason_codes: reasonCodes,
    score,
    score_band,
    timestamp: Date.now(),
    // Legacy aliases
    primary_cta_type: selectedCtaType,
    primary_cta_label: ctaDetails.label,
    primary_cta_target: finalTarget,
    route_to: routeOverride,
  };
  
  // Expose globally
  window.kp_decision = Object.freeze({ ...decision });
  
  return decision;
}

// ============================================
// Legacy Compatibility
// ============================================

/**
 * @deprecated Use decidePrimaryCTA instead
 */
export function decidePrimaryAction(
  kp_intel: KPIntel,
  kp_score: number,
  kp_score_band: ScoreBand
): KPDecision {
  const context: DecisionContext = {
    score: kp_score,
    score_band: kp_score_band,
    entry_source: kp_intel.entrySource,
    utm_source: kp_intel.utmSource,
    utm_medium: kp_intel.utmMedium,
    referrer: kp_intel.referrer,
    day_of_week: kp_intel.isSunday ? 0 : new Date().getDay(),
    hour: kp_intel.visitHour,
  };
  
  const decision = decidePrimaryCTA(context);
  
  // Add context reason codes
  if (kp_intel.isReturningVisitor) decision.reason_codes.push('ctx:returning');
  if (kp_intel.hasIntentSignal) decision.reason_codes.push('ctx:intent');
  if (kp_intel.isDesktop) decision.reason_codes.push('ctx:desktop');
  else if (kp_intel.isMobile) decision.reason_codes.push('ctx:mobile');
  if (kp_intel.totalPageViews > 3) decision.reason_codes.push('ctx:engaged');
  
  return decision;
}

/**
 * Get the current decision (if available)
 */
export function getCurrentDecision(): Readonly<KPDecision> | null {
  return window.kp_decision || null;
}

/**
 * Quick helper to decide from minimal inputs
 */
export function quickDecide(
  score: number,
  options?: {
    isSunday?: boolean;
    isNightTime?: boolean;
    visitHour?: number;
    entrySource?: string;
    utmSource?: string;
    utmMedium?: string;
    referrer?: string;
  }
): KPDecision {
  const now = new Date();
  const hour = options?.visitHour ?? now.getHours();
  const config = getConfig();
  
  // Calculate isNightTime if not provided
  let isNight = options?.isNightTime;
  if (isNight === undefined) {
    isNight = isNightHour(hour, config);
  }
  
  const dayOfWeek = options?.isSunday ? 0 : now.getDay();
  const scoreBand = getScoreBand(score, config);
  
  return decidePrimaryCTA({
    score,
    score_band: scoreBand,
    entry_source: options?.entrySource,
    utm_source: options?.utmSource,
    utm_medium: options?.utmMedium,
    referrer: options?.referrer,
    day_of_week: dayOfWeek,
    hour,
  });
}
